// My edge: where the trader actually makes money. Each setup (setup tags and Playbook plays)
// with its expectancy and a keep / watch / drop verdict, and the best and worst combinations of
// setup, session, market and side. Shown by EdgePage.js. Groups come from compareSlices.js.
import { choicesFor } from './compareSlices';
import { netPnl } from './pnl';
import { tagKind } from './tags';

export const MIN_COMBO = 5; // trades in a combination before it's shown
export const SOLID = 10; // trades before a setup gets a keep or drop verdict

export function edgeStats(list) {
  const nets = list.map(netPnl);
  const wins = nets.filter((n) => n > 0);
  const losses = nets.filter((n) => n < 0);
  const net = nets.reduce((a, b) => a + b, 0);
  const grossWin = wins.reduce((a, b) => a + b, 0);
  const grossLoss = -losses.reduce((a, b) => a + b, 0);
  return {
    trades: list, count: list.length, net,
    expectancy: list.length ? net / list.length : 0,
    winRate: list.length ? (wins.length / list.length) * 100 : 0,
    avgWin: wins.length ? grossWin / wins.length : 0,
    avgLoss: losses.length ? -grossLoss / losses.length : 0,
    profitFactor: grossLoss ? grossWin / grossLoss : (grossWin ? Infinity : null),
  };
}

// Keep: positive expectancy with enough trades and wins clearly bigger than losses in total.
// Drop: losing money over enough trades. Watch: everything else.
export function verdictOf(s) {
  if (s.count < SOLID) return 'watch';
  if (s.expectancy > 0 && (s.profitFactor === Infinity || s.profitFactor >= 1.2)) return 'keep';
  if (s.expectancy < 0) return 'drop';
  return 'watch';
}

// Setups: setup-kind tags, plus Playbook plays whose tag isn't already a setup tag.
export function setupChoices(trades, { library = [], plays = [] } = {}) {
  const tags = choicesFor('tags', trades).filter((c) => tagKind(c.id, library) === 'setup');
  const seen = new Set(tags.map((c) => c.id.toLowerCase()));
  const fromPlays = choicesFor('plays', trades, { plays }).filter((c) => !seen.has(c.id.toLowerCase()));
  return [...tags, ...fromPlays];
}

export function findEdge(trades, { library = [], plays = [] } = {}) {
  const dims = {
    setup: setupChoices(trades, { library, plays }),
    session: choicesFor('sessions', trades),
    market: choicesFor('instruments', trades),
    side: choicesFor('sides', trades),
  };
  const setups = dims.setup.map((c) => {
    const s = edgeStats(trades.filter(c.test));
    return { id: c.id, label: c.label, ...s, verdict: verdictOf(s) };
  }).sort((a, b) => b.net - a.net);

  // Two-way combinations; with setups, setup pairs first.
  const pairs = dims.setup.length
    ? [['setup', 'session'], ['setup', 'market'], ['setup', 'side'], ['session', 'market']]
    : [['session', 'market'], ['market', 'side'], ['session', 'side']];
  const combos = [];
  for (const [a, b] of pairs) {
    for (const x of dims[a]) {
      for (const y of dims[b]) {
        const list = trades.filter((t) => x.test(t) && y.test(t));
        if (list.length < MIN_COMBO) continue;
        combos.push({ key: `${a}:${x.id}|${b}:${y.id}`, parts: [[a, x.label], [b, y.label]], ...edgeStats(list) });
      }
    }
  }
  const best = combos.filter((c) => c.expectancy > 0).sort((p, q) => q.net - p.net).slice(0, 5);
  const worst = combos.filter((c) => c.expectancy < 0).sort((p, q) => p.net - q.net).slice(0, 5);
  return {
    overall: edgeStats(trades), setups, best, worst, hasSetups: dims.setup.length > 0,
    untagged: trades.filter((t) => !dims.setup.some((c) => c.test(t))).length,
  };
}
